import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import useApi from "../../../CustomHooks/useApi";

/* ── TABLE STYLES ── */
const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
const TD = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle whitespace-nowrap";
const TD_WRAP = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle break-words whitespace-normal";

/* ── HELPERS ── */
const StatusBadge = ({ status }) => {
    const s = String(status ?? "").toUpperCase();
    const cfg = {
        HOLD:     { bg: "bg-[#FEF3C7]", border: "border-amber-200",  text: "text-[#D97706]" },
        UNHOLD:   { bg: "bg-white",      border: "border-green-200",  text: "text-[#16A34A]" },
        ACTIVE:   { bg: "bg-white",      border: "border-green-200",  text: "text-[#16A34A]" },
        RELEASED: { bg: "bg-[#EFF6FF]",  border: "border-blue-200",   text: "text-[#0A2A4F]" },
        SUCCESS:  { bg: "bg-white",      border: "border-green-200",  text: "text-[#16A34A]" },
        FAILED:   { bg: "bg-[#FEE2E2]",  border: "border-red-200",    text: "text-[#DC2626]" },
    };
    const { bg, border, text } = cfg[s] || { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-500" };
    return (
        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-lg text-[12px] font-medium tracking-wide whitespace-nowrap border ${bg} ${border} ${text}`}>
            {status ?? "-"}
        </span>
    );
};

const ModeBadge = ({ mode }) => {
    const cfg = {
        ADD:    "bg-indigo-100 text-indigo-700 border-indigo-200",
        SUB:    "bg-orange-100 text-orange-700 border-orange-200",
        UNHOLD: "bg-green-100 text-green-700 border-green-200",
    };
    const cls = cfg[String(mode ?? "").toUpperCase()] || "bg-gray-100 text-gray-600 border-gray-200";
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold border ${cls}`}>
            {mode ?? "-"}
        </span>
    );
};

const fmt = (val) =>
    val !== undefined && val !== null
        ? `₹ ${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "-";

/* ── PAGINATION (1-based, matches Transactionsreport1) ── */
const getPaginationPages = (current, total) => {
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [];
    const delta = 2;
    let prev = null;
    for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
            if (prev !== null && i - prev > 1) pages.push("...");
            pages.push(i);
            prev = i;
        }
    }
    return pages;
};

/* ══════════════════════════════════════════════ */
const LienLedger = () => {
    const username = useSelector((state) => state.auth.employeeDetails.username);

    const { callApi: getAllHistory }  = useApi("/dmt/wallet/admin/lien-history", "GET");
    const { callApi: editLienHistory } = useApi("/dmt/wallet/admin/lien-history/edit", "PUT");

    const [data, setData]                 = useState([]);
    const [page, setPage]                 = useState(0);
    const [size, setSize]                 = useState(50);
    const [totalPages, setTotalPages]     = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLastPage, setIsLastPage]     = useState(false);
    const [loading, setLoading]           = useState(false);
    const [goToPage, setGoToPage]         = useState("");
    const [pageError, setPageError]       = useState("");
    const [excelLoading, setExcelLoading] = useState(false);

    /* ── edit modal state ── */
    const [editPopup, setEditPopup]     = useState({ open: false, row: null });
    const [editForm, setEditForm]       = useState({ lienAmount: "", remark: "" });
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError]     = useState("");

    /* ── apply API response ── */
    const applyResponse = (res, targetPage) => {
        const rows = res?.content ?? [];
        setData(rows);
        setPage(res?.number ?? targetPage);
        setTotalPages(res?.totalPages ?? 0);
        setTotalElements(res?.totalElements ?? rows.length);
        setIsLastPage(res?.last === true);
    };

    /* ── fetch all ── */
    const fetchAll = async (targetPage = 0, pageSize = size) => {
        setLoading(true);
        try {
            const res = await getAllHistory(null, `?page=${targetPage}&size=${pageSize}`);
            applyResponse(res, targetPage);
        } catch (err) {
            setData([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchAll(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── pagination ── */
    const handleFetchForPage = (targetPage) => {
        setPage(targetPage);
        fetchAll(targetPage, size);
    };

    const handleSizeChange = (s) => {
        setSize(s); setPage(0);
        fetchAll(0, s);
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage);
        if (isNaN(p) || p < 1 || p > totalPages) {
            setPageError(`Enter page between 1 to ${totalPages}`);
            return;
        }
        setPageError("");
        handleFetchForPage(p - 1);
    };

    /* ── edit handlers ── */
    const openEditModal = (row) => {
        setEditForm({ lienAmount: row.lienAmount ?? "", remark: row.remark ?? "" });
        setEditError("");
        setEditPopup({ open: true, row });
    };

    const closeEditModal = () => {
        setEditPopup({ open: false, row: null });
        setEditForm({ lienAmount: "", remark: "" });
        setEditError("");
    };

    const handleEditSubmit = async () => {
        if (editForm.lienAmount === "" || editForm.lienAmount === null) {
            setEditError("Lien amount is required.");
            return;
        }
        setEditLoading(true);
        setEditError("");
        try {
            const payload = {
                lien_id: editPopup.row.lienId,
                lien_amount: Number(editForm.lienAmount),
                remark: editForm.remark,
                modified_by: username,
            };
            await editLienHistory(payload);
            closeEditModal();
            fetchAll(page, size);
        } catch (err) {
            setEditError("Update failed. Please try again.");
        } finally {
            setEditLoading(false);
        }
    };

    /* ── excel download ── */
    const downloadExcel = async () => {
        setExcelLoading(true);
        try {
            const res = await getAllHistory(null, `?page=0&size=${totalElements || 1000}`);
            const rows = (res?.content ?? []).map((row, idx) => ({
                "#": idx + 1,
                "BP User ID": row.bpUserId ?? "-",
                "Merchant Name": row.merchantName ?? "-",
                "Mobile Number": row.mobileNumber ?? "-",
                "Lien ID": row.lienId ?? "-",
                "Mode": row.mode ?? "-",
                "Txn Amount": row.txnAmount ?? "-",
                "Lien Amount": row.lienAmount ?? "-",
                "Status": row.status ?? "-",
                "Remark": row.remark ?? "-",
                "Reason": row.reason ?? "-",
                "Modified By": row.modifiedBy ?? "-",
                "Merchant Wallet Bal.": row.merchantWalletBalance ?? "-",
                "AEPS Wallet Bal.": row.aepsWalletBalance ?? "-",
                "Created At": row.createdAt ?? "-",
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "LienHistory");
            const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "lien_history.xlsx");
        } catch (err) {
            console.error(err);
        } finally { setExcelLoading(false); }
    };

    /* ── derived ── */
    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd   = Math.min(page * size + data.length, totalElements);

    /* ══════════════ RENDER ══════════════ */
    return (
        <div className="w-full mx-auto pb-24">

            {/* HEADER */}
            <div className="mb-6 bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] rounded-2xl p-6 text-white shadow-lg">
                <h2 className="text-2xl font-bold">Lien History Ledger</h2>
                <p className="text-blue-200 text-sm mt-1">View and search lien history records</p>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow border overflow-hidden">

                {/* Card header */}
                <div className="px-5 py-4 border-b flex items-center justify-between gap-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-800">Lien History</h3>
                        <p className="text-xs text-gray-400 mt-0.5">{totalElements.toLocaleString("en-IN")} record{totalElements !== 1 ? "s" : ""}</p>
                    </div>
                    <button
                        onClick={downloadExcel}
                        disabled={excelLoading || totalElements === 0}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
                    >
                        {excelLoading ? "Downloading..." : `↓ Download Excel (${totalElements})`}
                    </button>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-10 h-10 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-gray-400 text-sm">Fetching lien history...</p>
                        </div>
                    ) : (
                        <table className="border-collapse text-sm" style={{ minWidth: 1400, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th className={TH}>#</th>
                                    <th className={TH}>Merchant</th>
                                    <th className={TH}>BP User ID</th>
                                    <th className={TH}>Lien ID</th>
                                    <th className={TH}>Mode</th>
                                    <th className={TH}>Txn Amount</th>
                                    <th className={TH}>Lien Amount</th>
                                    <th className={TH}>Status</th>
                                    <th className={TH}>Remark</th>
                                    <th className={TH}>Reason</th>
                                    <th className={TH}>Modified By</th>
                                    <th className={TH} style={{ width: 96, whiteSpace: "normal" }}>Merchant Wallet Bal.</th>
                                    <th className={TH} style={{ width: 80, whiteSpace: "normal" }}>AEPS Wallet Bal.</th>
                                    <th className={TH}>Created At</th>
                                    <th className={TH}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 ? (
                                    <tr>
                                        <td colSpan={15} className="border border-[#94A3B8] py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                                                <p className="text-gray-500 text-sm font-medium">No lien history records found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row, idx) => (
                                        <tr key={`${row.lienId}-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                            <td className={TD}>{page * size + idx + 1}</td>
                                            <td className={TD} style={{ minWidth: 160 }}>
                                                <p className="font-semibold text-gray-800 leading-tight">{row.merchantName ?? "-"}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{row.mobileNumber ?? ""}</p>
                                            </td>
                                            <td className={TD}>{row.bpUserId ?? "-"}</td>
                                            <td className={TD}>{row.lienId ?? "-"}</td>
                                            <td className={TD}><ModeBadge mode={row.mode} /></td>
                                            <td className={TD}>{fmt(row.txnAmount)}</td>
                                            <td className={`${TD} font-semibold text-red-600`}>{fmt(row.lienAmount)}</td>
                                            <td className={TD}><StatusBadge status={row.status} /></td>
                                            <td className={TD_WRAP} style={{ minWidth: 130, maxWidth: 210 }}>{row.remark ?? "-"}</td>
                                            <td className={TD_WRAP} style={{ minWidth: 130, maxWidth: 210 }}>{row.reason ?? "-"}</td>
                                            <td className={TD}>{row.modifiedBy ?? "-"}</td>
                                            <td className={TD} style={{ width: 96 }}>{fmt(row.merchantWalletBalance)}</td>
                                            <td className={TD} style={{ width: 80 }}>{fmt(row.aepsWalletBalance)}</td>
                                            <td className={TD}>
                                                {row.createdAt
                                                    ? new Date(row.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                                                    : "-"}
                                            </td>
                                            <td className={TD}>
                                                <button
                                                    onClick={() => openEditModal(row)}
                                                    className="inline-flex items-center gap-1.5 bg-[#0A2A4F] hover:bg-[#0d3566] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                                >
                                                    ✎ Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* PAGINATION (Transactionsreport1 style) */}
                {!loading && totalPages > 0 && (
                    <div className="border-t border-[#E5E7EB] bg-white px-4 py-2.5 flex items-center justify-between gap-3 overflow-x-auto flex-nowrap">

                        {/* LEFT: record range + Prev / pages / Next */}
                        <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                            {totalElements > 0 && (
                                <span className="text-[12px] text-gray-500 font-normal whitespace-nowrap mr-1">
                                    {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")}
                                </span>
                            )}
                            <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                ‹ Prev
                            </button>
                            {paginationPages.map((p, i) =>
                                p === "..." ? (
                                    <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                                ) : (
                                    <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                        className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${page === p - 1
                                            ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                            : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                        }`}>
                                        {p}
                                    </button>
                                )
                            )}
                            <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                Next ›
                            </button>
                        </div>

                        {/* RIGHT: Rows + Go to page */}
                        <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                            <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Rows</span>
                            {[50, 100, 200, 500].map(s => (
                                <button key={s} onClick={() => handleSizeChange(s)}
                                    className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${size === s
                                        ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                        : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F] hover:border-[#0A2A4F]/30"
                                    }`}>
                                    {s}
                                </button>
                            ))}
                            <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />
                            <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Go to</span>
                            <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                                onChange={e => { setGoToPage(e.target.value); setPageError(""); }}
                                className="h-8 w-20 px-2.5 border border-[#E5E7EB] rounded-lg bg-white text-[13px] font-normal text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                            <button onClick={handleGoToPage}
                                className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                                Go
                            </button>
                            {pageError && (
                                <span className="text-[12px] text-red-600 font-normal bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">
                                    ⚠ {pageError}
                                </span>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* ── EDIT MODAL ── */}
            {editPopup.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

                        {/* Modal header */}
                        <div className="bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] px-6 py-4 flex items-center justify-between">
                            <div>
                                <h3 className="text-white font-bold text-base">Edit Lien History</h3>
                                <p className="text-blue-200 text-xs mt-0.5">{editPopup.row?.lienId ?? ""}</p>
                            </div>
                            <button
                                onClick={closeEditModal}
                                disabled={editLoading}
                                className="text-white/70 hover:text-white text-xl leading-none disabled:opacity-40"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal body */}
                        <div className="px-6 py-5 space-y-4">

                            {/* Info row */}
                            <div className="bg-[#EFF6FF] border border-blue-100 rounded-xl px-4 py-3 text-sm text-[#0A2A4F]">
                                <span className="font-medium">Merchant:</span> {editPopup.row?.merchantName ?? "-"}
                                <span className="mx-3 text-blue-200">|</span>
                                <span className="font-medium">Mobile:</span> {editPopup.row?.mobileNumber ?? "-"}
                            </div>

                            {/* Lien Amount */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                    Lien Amount <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={editForm.lienAmount}
                                    onChange={(e) => setEditForm({ ...editForm, lienAmount: e.target.value })}
                                    placeholder="Enter lien amount"
                                    className="w-full border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]"
                                />
                            </div>

                            {/* Remark */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                    Remark
                                </label>
                                <textarea
                                    rows={3}
                                    value={editForm.remark}
                                    onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })}
                                    placeholder="Enter remark"
                                    className="w-full border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F] resize-none"
                                />
                            </div>

                            {/* Modified By (read-only) */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                    Modified By
                                </label>
                                <input
                                    type="text"
                                    value={username ?? ""}
                                    readOnly
                                    className="w-full border border-[#E5E7EB] rounded-xl px-4 py-2.5 text-sm text-gray-500 bg-gray-50 cursor-not-allowed"
                                />
                            </div>

                            {/* Error */}
                            {editError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-sm text-red-600">
                                    {editError}
                                </div>
                            )}
                        </div>

                        {/* Modal footer */}
                        <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-end gap-3">
                            <button
                                onClick={closeEditModal}
                                disabled={editLoading}
                                className="px-5 py-2.5 rounded-xl border border-[#CBD5E1] text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditSubmit}
                                disabled={editLoading}
                                className="px-6 py-2.5 rounded-xl bg-[#0A2A4F] hover:bg-[#0d3566] text-white text-sm font-semibold disabled:opacity-50 transition flex items-center gap-2"
                            >
                                {editLoading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                                        Saving...
                                    </>
                                ) : "Submit"}
                            </button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default LienLedger;
